import { useEffect, useState } from "react";
import { Lock, Menu, X } from "lucide-react";
import Monogram from "./Monogram";
import { scrollToId } from "@/lib/lenis";
import { WEDDING } from "@/data/wedding";

const LINKS = [
  { id: "#accueil", label: "Accueil", testid: "nav-link-accueil" },
  { id: "#informations", label: "Informations", testid: "nav-link-programme" },
];

export default function Navbar({ onAdminOpen }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    setMobileOpen(false);
    scrollToId(id);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
        scrolled
          ? "border-b border-line bg-sand/85 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a
          href="#accueil"
          onClick={(e) => go(e, "#accueil")}
          data-testid="nav-brand"
          className="flex items-center gap-3"
        >
          <Monogram className="h-10 w-10" />
          <span className="hidden flex-col leading-none sm:flex">
            <span className="font-serif text-lg font-semibold tracking-wide">
              {WEDDING.bride} &amp; {WEDDING.groom}
            </span>
            <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-fog">
              {WEDDING.dateShort}
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={l.testid}
              onClick={(e) => go(e, l.id)}
              className="group relative text-sm font-medium text-taupe transition-colors hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-terracotta transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
          <a
            href="#rsvp"
            data-testid="nav-link-rsvp"
            onClick={(e) => go(e, "#rsvp")}
            className="rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-sand transition-colors duration-300 hover:bg-terracotta"
          >
            Répondre à l'invitation
          </a>
          <button
            type="button"
            data-testid="nav-admin-trigger"
            onClick={onAdminOpen}
            title="Espace mariés"
            aria-label="Espace mariés"
            className="rounded-full p-2 text-fog transition-colors hover:bg-cream hover:text-terracotta"
          >
            <Lock className="h-4 w-4" />
          </button>
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            data-testid="nav-admin-trigger-mobile"
            onClick={onAdminOpen}
            aria-label="Espace mariés"
            className="rounded-full p-2 text-fog transition-colors hover:bg-cream hover:text-terracotta"
          >
            <Lock className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
            className="rounded-full p-2 text-ink transition-colors hover:bg-cream"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-line bg-sand/95 px-6 py-6 backdrop-blur-md md:hidden">
          <div className="flex flex-col gap-5">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={(e) => go(e, l.id)}
                className="font-serif text-2xl text-ink"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#rsvp"
              data-testid="nav-link-rsvp-mobile"
              onClick={(e) => go(e, "#rsvp")}
              className="inline-flex w-fit rounded-full bg-ink px-6 py-3 text-sm font-medium text-sand"
            >
              Répondre à l'invitation
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
